import { useCallback, useEffect, useRef, useState, type FocusEvent, type ReactNode, type RefObject } from 'react'
import { twMerge } from 'tailwind-merge'
import { Popover, useKeyboardFinalFocus } from '@highlight/ui'
import type { Popover as BasePopover } from '@base-ui/react/popover'
import { FadingScrollable } from '@renderer/chat/components/elements/FadingScrollable'
import { useSidepanelState } from '@renderer/overlay/atoms'
import { isPopoverSwap, isRowStep, onRowStep } from '../../utils/row-step'

type PopoverChangeDetails = BasePopover.Root.ChangeEventDetails

/** Where a to-do's popover sits against its row: under it, pinned at its right edge, so a wider card widens it to the left. */
const TODO_POPOVER_POSITION = { side: 'bottom', align: 'end', sideOffset: 4 } as const

/** A to-do popover's frame and its scale-and-fade in and out, without its size. */
const TODO_POPOVER_FRAME =
  'flex max-w-[var(--available-width)] origin-[var(--transform-origin)] overflow-hidden p-0 transition-[opacity,transform] data-[ending-style]:scale-[0.97] data-[starting-style]:scale-[0.97] data-[starting-style]:opacity-0 motion-reduce:transition-none'

/** Whether a press landed on the row a popover is anchored to, which toggles the popover itself. */
function isRowPress(row: RefObject<Element> | undefined, details: PopoverChangeDetails) {
  const target = details.event?.target
  return (
    details.reason === 'outside-press' &&
    target instanceof Element &&
    !!row?.current?.contains(target.closest('[data-slot="cell"]'))
  )
}

/**
 * Open state for a sidepanel row's popover. When it's anchored to the `row` rather
 * than triggered by it, the row's own click toggles it, so a press on that row
 * isn't also taken as the outside press that closes it, or a second click would
 * close it and reopen it straight away. Presses on the row's hover buttons still
 * close it.
 *
 * Arrowing onto the row previews the popover: it opens without taking focus, so
 * the arrow keys keep stepping through the rows, and closes as they step off.
 * Enter or a click on the row opens it for real. Stepping from one row's popover
 * to the next swaps them without fading either.
 */
export function useTodoPopover(row?: RefObject<Element>) {
  const [open, setOpen] = useState(false)
  // Left set while a preview closes, so the popup doesn't hand focus back to a row already stepped off.
  const [previewing, setPreviewing] = useState(false)
  const [swapping, setSwapping] = useState(false)
  const panelOpen = useSidepanelState() === 'open'

  // The popup portals out of the panel, so it would otherwise outlive it.
  useEffect(() => {
    if (!panelOpen) setOpen(false)
  }, [panelOpen])

  // A step closes it however it was opened, with focus on its row or inside it. One landing back
  // on its row reopens it as a preview.
  useEffect(() => {
    if (!open) return
    return onRowStep(() => {
      setSwapping(isPopoverSwap())
      setOpen(false)
    })
  }, [open])

  const onOpenChange = useCallback(
    (next: boolean, details: PopoverChangeDetails) => {
      if (!next && isRowPress(row, details)) return
      // A row that's its popover's trigger opens a preview for real, as `toggle` does.
      // `swapping` goes with it: a preview reached by a step carries it, and left
      // set it would keep the now-real popup from handing focus back to its row.
      if (!next && previewing && details.reason === 'trigger-press') {
        setPreviewing(false)
        setSwapping(false)
        return
      }
      if (next) setPreviewing(false)
      // Closed by focus leaving it for the next row's.
      setSwapping(!next && isPopoverSwap())
      setOpen(next)
    },
    [row, previewing],
  )
  const toggle = useCallback(() => {
    if (open && !previewing) {
      setOpen(false)
      return
    }
    setPreviewing(false)
    setSwapping(false)
    setOpen(true)
  }, [open, previewing])
  const close = useCallback(() => {
    setSwapping(false)
    setOpen(false)
  }, [])

  const onRowFocus = useCallback((event: FocusEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget || !isRowStep()) return
    setPreviewing(true)
    setSwapping(isPopoverSwap())
    setOpen(true)
  }, [])
  const onRowBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      const to = event.relatedTarget
      // A press inside the popup lands focus there, and leaves the preview up. A step closes it itself.
      if (!previewing || isRowStep() || event.currentTarget.contains(to) || to?.closest('[role="dialog"]')) return
      setOpen(false)
    },
    [previewing],
  )

  return { open: open && panelOpen, previewing, swapping, onOpenChange, toggle, close, onRowFocus, onRowBlur }
}

/** Moves focus into a previewed popover once it's opened for real, as it would have taken it opening. */
export function useFocusWhenPreviewOpens(open: boolean, previewing: boolean, target: RefObject<HTMLElement>) {
  const wasShowingPreview = useRef(false)
  useEffect(() => {
    if (open && wasShowingPreview.current && !previewing) target.current?.focus()
    wasShowingPreview.current = open && previewing
  }, [open, previewing, target])
}

interface TodoPopoverProps {
  open: boolean
  onOpenChange: (open: boolean, details: PopoverChangeDetails) => void
  anchor: RefObject<Element>
  /** Open without focus, from `useTodoPopover`. */
  previewing?: boolean
  /** Swapping with another row's, from `useTodoPopover`, so it neither fades in nor out. */
  swapping?: boolean
  /** What Highlight wrote about the to-do, on a card in a column of its own. */
  aside?: ReactNode
  /** Held at the foot of the card, under `aside` however long it runs. */
  asidePrompt?: ReactNode
  /** The title and the user's own content. */
  children: ReactNode
  /** Settings held at the bottom however much content sits above them. */
  footer?: ReactNode
}

/**
 * A to-do opened from its row, laid out like the meeting popover with its
 * schedule: a card on the left, the to-do's title and moves on the right. Short
 * and fixed in height, the shape of a note rather than a page: a three-line title
 * fits, and longer content scrolls.
 */
export function TodoPopover({
  open,
  onOpenChange,
  anchor,
  previewing = false,
  swapping = false,
  aside,
  asidePrompt,
  children,
  footer,
}: TodoPopoverProps) {
  const hasCard = !!aside || !!asidePrompt
  const bodyRef = useRef<HTMLDivElement>(null)
  const finalFocus = useKeyboardFinalFocus(open)
  useFocusWhenPreviewOpens(open, previewing, bodyRef)
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Popover.Content
        anchor={anchor}
        {...TODO_POPOVER_POSITION}
        // The default, the first tabbable, would be a link at the foot of the card, scrolled into view.
        initialFocus={previewing ? false : bodyRef}
        // Focus stays on the row a step went to.
        finalFocus={previewing || swapping ? false : finalFocus}
        className={twMerge(
          TODO_POPOVER_FRAME,
          swapping && 'transition-none',
          // Pinned at the row's right edge and starting 20px into it, so the to-do's text lines up with the
          // row's and sits 16px in from every edge: this 4px plus 12px of each column's own. The card
          // widens it to the left.
          'h-[min(208px,var(--available-height))] w-[calc(var(--anchor-width)-20px)] gap-4 p-1',
          hasCard && 'w-[calc(var(--anchor-width)+180px)]',
        )}
      >
        {/* The meeting popover's brief card: gray, with the popover's corner less the 4px between them,
            in the same squircle, so the two curves run parallel. */}
        {hasCard && (
          <div className="corner-squircle bg-element flex min-w-0 flex-1 flex-col overflow-hidden rounded-[calc(var(--radius-lg)-4px)]">
            <FadingScrollable className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-3">
              {aside}
            </FadingScrollable>
            {asidePrompt && <div className="shrink-0 p-1.5 pt-0">{asidePrompt}</div>}
          </div>
        )}
        {/* Spans the row's title: 36px in from its left (the cell's 12px padding, the 16px mark and its
            8px gap) to 12px from its right, which is this 8px plus the popover's 4px. */}
        <div className="ml-auto flex w-[calc(var(--anchor-width)-40px)] shrink-0 flex-col pr-2">
          <FadingScrollable
            ref={bodyRef}
            tabIndex={-1}
            className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pt-3 focus-visible:outline-none"
          >
            {children}
          </FadingScrollable>
          {footer && <div className="flex shrink-0 flex-wrap items-center gap-2 pb-3 pt-2">{footer}</div>}
        </div>
      </Popover.Content>
    </Popover>
  )
}
